/* Entrance Handles: clicking a gallery image selects the matching finish.
 *
 * Nick, 23 Sep 2026: "when someone clicks on the bronze handle picture or the black one,
 * make the multi choice options for finishes and stuff match the picture that is being shown."
 *
 * Dawn already does the reverse (pick a finish, the gallery jumps to that finish's image).
 * This closes the loop. Two ways an image is tied to a finish, in this order:
 *   1. the image is assigned to a variant in Shopify admin (variant.featured_media);
 *   2. the image's alt text names exactly one of the finish option's values
 *      ("... in matte black powder coat ..." selects "Powder Coat - Black").
 * "Custom Colour" is never auto-selected. Products with no finish option do nothing.
 *
 * The data comes from a JSON block that sections/main-product.liquid renders, so there is no
 * extra request. After we flip the option, Dawn fetches the new variant and would normally
 * jump the gallery to that variant's own image; we hold the image the shopper clicked instead,
 * because they are looking at it on purpose.
 */
(function () {
  'use strict';
  if (window.__ehImageVariantSync) return;
  window.__ehImageVariantSync = true;

  var COLOUR_OPTION = /^(colou?r|finish)$/i;
  var NEVER = /custom|other|as specified/i;

  function tokensFor(value) {
    // "Powder Coat - Black" -> ["black"]; "Brushed Stainless" -> ["stainless", "316"];
    // "Brushed Nickel" -> ["nickel"]; "Black" -> ["black"]
    var v = String(value).toLowerCase();
    if (NEVER.test(v)) return [];
    v = v.replace(/powder[\s-]*coat(ed)?/g, ' ').replace(/[-:()]/g, ' ');
    var words = v.split(/\s+/).filter(function (w) {
      return w && !/^(brushed|satin|matte|matt|polished|natural|antique|solid|finish|coat|in)$/.test(w);
    });
    var out = words.slice();
    if (words.indexOf('stainless') !== -1) out.push('316');
    return out;
  }

  function init(section) {
    var data = section.querySelector('script[data-eh-media-finish]');
    var gallery = section.querySelector('media-gallery');
    if (!data || !gallery || !section.querySelector('variant-selects')) return;
    // Dawn re-renders <variant-selects> after every variant change, so never cache it.
    var picker = { querySelectorAll: function (sel) {
      var el = section.querySelector('variant-selects');
      return el ? el.querySelectorAll(sel) : [];
    } };
    var cfg;
    try { cfg = JSON.parse(data.textContent); } catch (e) { return; }
    if (!cfg || !cfg.option || !cfg.values || !cfg.values.length) return;

    var sectionId = section.dataset.ehSection || (gallery.id || '').replace(/^MediaGallery-/, '');
    var byMedia = {};              // mediaId -> value, from variant assignment
    Object.keys(cfg.variantMedia || {}).forEach(function (mid) { byMedia[mid] = cfg.variantMedia[mid]; });

    var valueTokens = cfg.values.map(function (v) { return { value: v, tokens: tokensFor(v) }; })
      .filter(function (e) { return e.tokens.length; });

    function finishForMedia(mediaId) {
      if (byMedia[mediaId]) return byMedia[mediaId];
      var alt = ((cfg.media || {})[mediaId] || '').toLowerCase();
      if (!alt) return null;
      var hits = valueTokens.filter(function (e) {
        return e.tokens.some(function (t) { return alt.indexOf(t) !== -1; });
      });
      return hits.length === 1 ? hits[0].value : null;
    }

    // Dawn's capture leaves a trailing newline in the input name ("Color-2\n"), so match trimmed.
    function controls(tag) {
      return Array.prototype.filter.call(picker.querySelectorAll(tag), function (el) {
        return (el.name || '').trim() === cfg.option.inputName;
      });
    }

    function currentValue() {
      var checked = controls('input[type="radio"]').filter(function (i) { return i.checked; })[0];
      if (checked) return checked.value;
      var sel = controls('select')[0];
      return sel ? sel.value : null;
    }

    function selectValue(value) {
      var radio = controls('input[type="radio"]').filter(function (i) { return i.value === value; })[0];
      if (radio) {
        if (radio.checked) return false;
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
      var sel = controls('select')[0];
      if (sel && sel.value !== value) {
        sel.value = value;
        sel.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
      return false;
    }

    // Hold the clicked image while Dawn swaps the variant. setActiveMedia(id, true) is the
    // variant-driven call; we redirect it to the held image for a short window.
    var hold = null, holdTimer = 0;
    var original = gallery.setActiveMedia;
    if (typeof original === 'function' && !gallery.__ehPatched) {
      gallery.__ehPatched = true;
      gallery.setActiveMedia = function (mediaId, prepend) {
        if (hold && prepend && mediaId !== hold) {
          // let Dawn do its DOM add/remove of variant images, then keep the shopper's pick
          try { original.call(this, mediaId, true); } catch (e) {}
          var self = this;
          window.requestAnimationFrame(function () {
            try { original.call(self, hold, false); } catch (e) {}
          });
          return;
        }
        return original.call(this, mediaId, prepend);
      };
    }

    function onPick(fullMediaId) {
      var mediaId = String(fullMediaId).replace(sectionId + '-', '');
      var want = finishForMedia(mediaId);
      if (!want || want === currentValue()) return;
      hold = fullMediaId;
      clearTimeout(holdTimer);
      holdTimer = setTimeout(function () { hold = null; }, 4000);
      selectValue(want);
    }

    // Thumbnail clicks (desktop and phone), and swipes on the phone slider.
    gallery.addEventListener('click', function (ev) {
      var li = ev.target.closest('.thumbnail-list__item[data-target]');
      if (li) onPick(li.dataset.target);
    });
    var viewer = gallery.querySelector('[id^="GalleryViewer"]');
    if (viewer) {
      viewer.addEventListener('slideChanged', function (ev) {
        var el = ev.detail && ev.detail.currentElement;
        if (!el || !el.dataset.mediaId) return;
        // a slide change caused by our own hold/variant swap is not a shopper action
        if (hold) return;
        onPick(el.dataset.mediaId);
      });
    }
  }

  function boot() {
    document.querySelectorAll('[data-eh-image-variant-sync]').forEach(function (s) {
      if (s.__ehSyncInit) return;
      s.__ehSyncInit = true;
      init(s);
    });
  }
  // This script is deferred ahead of Dawn's media-gallery.js, so wait until the <media-gallery>
  // element is defined and upgraded; patching setActiveMedia before that would patch nothing.
  function ready() {
    var w = window.customElements && customElements.whenDefined('media-gallery');
    if (w && w.then) w.then(boot, boot); else boot();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready);
  else ready();
  document.addEventListener('shopify:section:load', ready);
})();
